import { LinkedProjectConfig } from '../types';
import { asDay } from './serviceLogUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ABRECHNUNGSART EINER TOCHTER — und ihre Geschichte
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine Tochter rechnet entweder als TAGESPAUSCHALE ab (der Kalendertag ist die
 * Einheit; Mutter und Toechter am selben Tag sind EINE Leistung) oder EINZELN
 * (jede Tochter hat ihre eigene Leistung, derselbe Tag deckt nichts).
 *
 * Bisher liess sich das nur beim Verknuepfen festlegen. Wer es spaeter aendern
 * wollte, musste trennen und neu verknuepfen — und dabei wandern die Leistungen
 * mit (`applyLinkTransfer`), was den Vorgang zu einem Umbau statt zu einer
 * Einstellung macht.
 *
 * ─── Warum eine Historie und nicht nur ein Wert ────────────────────────────
 *
 * Die Abrechnungsart entscheidet rueckwirkend darueber, ob eine ALTE Begehung
 * als abgerechnet gilt: bei Tagespauschale deckt die Buchung der Mutter am
 * selben Tag die Tochter mit (`istTagGedeckt`). Stuende hier nur der aktuelle
 * Wert, machte ein Umschalten auf „einzeln" jede frueher gedeckte Begehung
 * schlagartig wieder zu einer offenen Forderung — bei 5284-1 waeren das auf
 * einen Schlag 13 Meldungen ueber 9.775 EUR gewesen, obwohl sich an den
 * Rechnungen von damals nichts geaendert hat.
 *
 * Deshalb merkt sich die Umstellung ihren TAG. Jede Begehung wird nach der
 * Art beurteilt, die an IHREM Tag galt. Die Umstellung wirkt nach vorn, nie
 * zurueck — genau wie ein Preisaenderung im Vertrag.
 */

export type LinkedBillingMode = 'daily_flat' | 'individual';

export const TAGESPAUSCHALE: LinkedBillingMode = 'daily_flat';
export const EINZELN: LinkedBillingMode = 'individual';

/** Der Beginn der Zeitrechnung — die Art, die vor der ersten Umstellung galt. */
const SEIT_JE = '1970-01-01';

export const billingModeLabelDe = (mode?: LinkedBillingMode | string | null): string =>
    mode === TAGESPAUSCHALE ? 'Tagespauschale' : mode === EINZELN ? 'Einzelabrechnung' : 'unbekannt';

/** Heutiger Kalendertag in Ortszeit, `YYYY-MM-DD`. */
export const heute = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Welche Abrechnungsart galt an diesem Tag?
 *
 * Ohne Historie (Altbestand, nie umgestellt) gilt der eingetragene Wert fuer
 * ALLE Tage — das ist genau das bisherige Verhalten und aendert an keinem
 * bestehenden Projekt etwas.
 *
 * Ein unbrauchbarer oder fehlender Tag liefert die AKTUELLE Art. Zu raten,
 * es sei die alte, koennte eine Begehung faelschlich als gedeckt ausweisen.
 */
export const billingModeAt = (
    config: LinkedProjectConfig | null | undefined,
    tag?: string | null,
): LinkedBillingMode | undefined => {
    if (!config) return undefined;
    const historie = config.billingModeHistory;
    const aktuell = config.billingMode as LinkedBillingMode | undefined;
    if (!Array.isArray(historie) || historie.length === 0) return aktuell;

    const t = asDay(tag || undefined);
    if (!t) return aktuell;

    // Der letzte Eintrag, dessen Geltungsbeginn nicht in der Zukunft liegt.
    let treffer: LinkedBillingMode | undefined;
    for (const e of [...historie].sort((a, b) => String(a.from).localeCompare(String(b.from)))) {
        if (String(e.from) <= t) treffer = e.mode as LinkedBillingMode;
    }
    // Liegt der Tag VOR jeder Umstellung, gilt der aelteste vermerkte Stand.
    return treffer ?? (historie[0]?.mode as LinkedBillingMode | undefined) ?? aktuell;
};

/**
 * Die Umstellung — als reine Rechnung, damit sie ohne Oberflaeche pruefbar ist.
 *
 * Bei der ERSTEN Umstellung wird die bisherige Art rueckwirkend festgeschrieben
 * (`from: 1970-01-01`), sonst waere nicht mehr feststellbar, was vorher galt.
 * Danach wird nur noch angehaengt; die Historie wird nie umgeschrieben.
 *
 * Gibt `null` zurueck, wenn nichts zu tun ist (gleiche Art, keine Tochter) —
 * der Aufrufer speichert dann auch nichts.
 */
export const switchBillingMode = (
    config: LinkedProjectConfig | null | undefined,
    neu: LinkedBillingMode,
    abTag: string = heute(),
): LinkedProjectConfig | null => {
    if (!config || config.role !== 'child') return null;
    const alt = (config.billingMode as LinkedBillingMode | undefined) ?? TAGESPAUSCHALE;
    if (alt === neu) return null;

    const historie = Array.isArray(config.billingModeHistory) && config.billingModeHistory.length > 0
        ? [...config.billingModeHistory]
        : [{ mode: alt, from: SEIT_JE }];

    return {
        ...config,
        billingMode: neu,
        billingModeHistory: [...historie, { mode: neu, from: abTag }],
    };
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * WER HAT DEN TAG SCHON GEBUCHT — und zaehlt das gegen die Mutter?
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Eine Tochter bucht IMMER in das Protokoll der Mutter, in beiden
 * Abrechnungsarten (`handleConfirmMainBilling`). Der Riegel auf der
 * Mutter-Seite fragte bisher nur „steht an diesem Tag schon eine
 * Begehungsbuchung in meinem Protokoll?" — und traf damit auch die Buchung
 * einer EINZELN abrechnenden Tochter.
 *
 * Die Folge war eine Reihenfolge-Abhaengigkeit, die es nicht geben darf:
 *
 *   Mutter zuerst, dann Tochter → beide gebucht (richtig)
 *   Tochter zuerst, dann Mutter → Mutter gesperrt (falsch)
 *
 * Bei EINZELabrechnung sagt die Buchung einer Tochter nichts ueber die eigene
 * Begehung der Mutter. Nur bei TAGESPAUSCHALE ist der Tag die Einheit, und nur
 * dann darf die Tochter die Mutter sperren.
 */

/** Die Kind-Kennung aus `RPT-CHILD-<uuid>-<nr>`; sonst `null`. */
export const childIdFromReportRef = (ref?: string | null): string | null => {
    const treffer = /^RPT-CHILD-([0-9a-fA-F-]{36})-\d+$/.exec(String(ref || ''));
    return treffer ? treffer[1] : null;
};

/** Stammt dieser Protokolleintrag aus einer Tochter? */
export const istKindBuchung = (entry: { reportRef?: string; description?: string } | null | undefined): boolean =>
    !!childIdFromReportRef(entry?.reportRef) || String(entry?.description || '').startsWith('Tochter-Projekt "');

/**
 * Sperrt dieser Eintrag im Mutterprotokoll eine EIGENE Begehung der Mutter am
 * selben Tag?
 *
 * · Eigene Buchung der Mutter → JA. Zwei eigene Begehungen an einem Tag sind
 *   der dokumentierte Doppelbuchungsfall; daran wird nichts aufgeweicht.
 * · Buchung einer TAGESPAUSCHALEN-Tochter → JA. Der Tag ist die Einheit.
 * · Buchung einer EINZELN abrechnenden Tochter → NEIN. Sie hat ihre eigene
 *   Leistung und sagt ueber die Mutter nichts.
 * · Tochter nicht zuzuordnen (Alteintrag ohne `reportRef`) → JA. Der Riegel ist
 *   WEICH und im Dialog ueberstimmbar; ihn im Zweifel zu setzen kostet einen
 *   Klick, ihn wegzulassen eine Doppelbuchung.
 */
export const sperrtEigeneBegehung = (
    entry: { reportRef?: string; description?: string } | null | undefined,
    /** Art JE KIND, bereits auf den Begehungstag aufgeloest (`billingModeAt`). */
    modusJeKind: Map<string, LinkedBillingMode | undefined>,
): boolean => {
    if (!istKindBuchung(entry)) return true;
    const kindId = childIdFromReportRef(entry?.reportRef);
    if (!kindId) return true;
    const modus = modusJeKind.get(kindId);
    if (modus === undefined) return true;
    return modus === TAGESPAUSCHALE;
};

/**
 * Stammt dieser Eintrag im Mutterprotokoll von GENAU DIESER Tochter?
 *
 * Gebraucht auf der Tochter-Seite bei EINZELabrechnung: dort darf nur die
 * eigene Zweitbuchung desselben Tages warnen. Die Buchung der Mutter oder einer
 * Schwester ist eine andere Leistung und geht diese Tochter nichts an — genau
 * die Verwechslung war der Reihenfolge-Fehler auf der Mutter-Seite.
 *
 * Zwei Wege, weil der Bestand zwei Schreibweisen kennt:
 *  · `RPT-CHILD-<kindId>-<nr>` — eindeutig, seit es den Ref gibt.
 *  · `Tochter-Projekt "<Name>": …` — Alteintraege ohne Ref. Der Name ist das
 *    einzige, was sie ueber ihre Herkunft sagen.
 *
 * Laesst sich ein Eintrag WEDER ueber den Ref NOCH ueber den Namen zuordnen,
 * gilt er als fremd und warnt nicht. Anders als auf der Mutter-Seite ist das
 * hier die richtige Richtung: dort steht im Zweifel die eigene Buchung der
 * Mutter dahinter, hier dagegen mit gleicher Wahrscheinlichkeit eine
 * SCHWESTER — und die zu unterstellen brachte genau den Fehlalarm zurueck,
 * der eben beseitigt wurde.
 */
export const istBuchungDerTochter = (
    entry: { reportRef?: string; description?: string } | null | undefined,
    childId: string,
    childName?: string | null,
): boolean => {
    const ausRef = childIdFromReportRef(entry?.reportRef);
    if (ausRef) return ausRef === childId;
    const name = (childName || '').trim();
    if (!name) return false;
    return String(entry?.description || '').startsWith(`Tochter-Projekt "${name}"`);
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE KIND-SEITE — wer sperrt eine Tochter, und wer geht sie nichts an?
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Mutter-Seite hat den Reihenfolge-Fehler bereits hinter sich
 * (`sperrtEigeneBegehung`): sie loest die Art JE KIND auf, statt jede fremde
 * Buchung des Tages gegen sich gelten zu lassen. Auf der Kind-Seite fehlte
 * genau das — dort galt bei Tagespauschale schlicht der ERSTE Eintrag des
 * Tages, ohne zu fragen, woher er stammt.
 *
 * Falsch ist das, sobald eine Mutter MEHRERE Toechter hat. Bucht an einem Tag
 * zuerst eine EINZELN abrechnende Schwester in das Protokoll der Mutter, dann
 * fand die Tagespauschalen-Tochter dort einen Eintrag und war gesperrt — obwohl
 * die Schwester ihre eigene Leistung abrechnet und ueber diesen Tag nichts
 * aussagt. Wieder eine Reihenfolge-Abhaengigkeit, nur eine Ebene tiefer:
 *
 *   Tochter zuerst, dann Schwester → beide gebucht (richtig)
 *   Schwester zuerst, dann Tochter → Tochter gesperrt (falsch)
 *
 * Der Riegel ist weich und im Dialog ueberstimmbar, es entsteht also kein
 * Geldschaden — aber Laerm, und genau der hat bei 5284-1 schon einmal 13
 * Meldungen erzeugt. Eine Warnung, die man gewohnheitsmaessig wegklickt,
 * schuetzt beim naechsten echten Fall nicht mehr.
 */

/**
 * Warum ein Eintrag im Mutterprotokoll diese Tochter sperrt — `null` heisst:
 * er geht sie nichts an. Der Grund traegt den Meldungstext; „Schwester" muss
 * anders klingen als „Mutter", sonst sucht der Nutzer den Beleg am falschen Ort.
 */
export type TochterSperrgrund = 'eigene' | 'mutter' | 'schwester';

/**
 * Sperrt dieser Eintrag im Mutterprotokoll die Begehung DIESER Tochter am
 * selben Tag?
 *
 * · Eigene Buchung dieser Tochter → IMMER, in beiden Abrechnungsarten. Zwei
 *   Buchungen derselben Tochter an einem Tag sind der dokumentierte
 *   Doppelbuchungsfall; daran wird nichts aufgeweicht.
 * · Buchung der MUTTER → nur bei Tagespauschale. Dann ist der Tag die Einheit
 *   und die Mutter hat ihn verbraucht. Rechnet die Tochter einzeln ab, hat sie
 *   ihre eigene Leistung und die Mutter geht sie nichts an.
 * · Buchung einer SCHWESTER → nur, wenn BEIDE Seiten den Tag als Einheit sehen:
 *   diese Tochter mit Tagespauschale UND die Schwester an diesem Tag ebenfalls.
 *   Eine einzeln abrechnende Schwester sperrt nie — sie rechnet ihre eigene
 *   Leistung ab.
 * · Herkunft nicht aufloesbar (Alteintrag ohne `reportRef`, Art der Schwester
 *   nicht lesbar) → sperren. Der Riegel ist WEICH; ihn im Zweifel zu setzen
 *   kostet einen Klick, ihn wegzulassen eine Doppelbuchung. Dieselbe Richtung
 *   wie auf der Mutter-Seite.
 *
 * Eine fehlende eigene Art wird wie Tagespauschale behandelt: das ist der
 * Vorgabewert der Verknuepfung (`switchBillingMode`) und zugleich die
 * sperrende, also die sichere Richtung.
 */
export const sperrtFremdeBuchungDieTochter = (
    entry: { reportRef?: string; description?: string } | null | undefined,
    opts: {
        /** Projekt-Kennung DIESER Tochter. */
        childId: string;
        /** Ihr Projektname — die einzige Spur, die Alteintraege ohne Ref haben. */
        childName?: string | null;
        /** Ihre Art, bereits auf den Begehungstag aufgeloest (`billingModeAt`). */
        modus: LinkedBillingMode | undefined;
        /** Art JE SCHWESTER, ebenfalls auf den Begehungstag aufgeloest. */
        modusJeSchwester: Map<string, LinkedBillingMode | undefined>;
    },
): TochterSperrgrund | null => {
    // Die eigene Buchung zuerst: sie sperrt unabhaengig von jeder Art, und die
    // Frage nach Mutter oder Schwester stellt sich dann gar nicht.
    if (istBuchungDerTochter(entry, opts.childId, opts.childName)) return 'eigene';

    // Ab hier ist der Eintrag fremd. Rechnet diese Tochter einzeln ab, hat sie
    // ihre eigene Leistung — weder Mutter noch Schwester verbrauchen sie.
    if (opts.modus === EINZELN) return null;

    // Kein `RPT-CHILD-`-Ref und kein „Tochter-Projekt"-Vermerk: die Mutter hat
    // selbst gebucht. Bei Tagespauschale ist der Tag damit verbraucht.
    if (!istKindBuchung(entry)) return 'mutter';

    // Eine Schwester. Sie sperrt nur, wenn sie den Tag ebenfalls als Einheit
    // sieht. Laesst sie sich nicht aufloesen — Alteintrag ohne Ref oder Art
    // nicht gelesen —, wird im Zweifel gesperrt.
    const schwesterId = childIdFromReportRef(entry?.reportRef);
    if (!schwesterId) return 'schwester';
    const schwesterModus = opts.modusJeSchwester.get(schwesterId);
    if (schwesterModus === undefined) return 'schwester';
    return schwesterModus === TAGESPAUSCHALE ? 'schwester' : null;
};
